import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import MobileGameRow from './MobileGameRow';
import MobileGameDetail from './MobileGameDetail';
import MobileGenericLockCard from './MobileGenericLockCard';
import { useGameContext } from '../../context/GameContext';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import { useFilterContext } from '../../context/FilterContext';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import { applyFilters } from '../../utils/filterUtils';
import styles from './MobileWeeklyPickSheet.module.css';

const MobileWeeklyPickSheet = ({ displayedWeek, onWeekChange }) => {
  const {
    games,
    locksCache,
    toggleWatchPick,
    lockPicks,
    user,
    getMaxPicksForWeek
  } = useGameContext();

  const { currentWeek } = useCurrentWeek();
  const { filters, hasActiveFilters } = useFilterContext();
  const { canSeeLocks } = useLeagueSettings();
  const [selectedGame, setSelectedGame] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const sortedGames = useMemo(() => {
    // Apply filters to games
    const filteredGames = applyFilters(games, filters);
    return [...filteredGames].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  }, [games, filters]);

  // Check if user has reached max picks for the current week
  const hasReachedMaxPicks = useCallback(() => {
    if (!displayedWeek || !locksCache[displayedWeek]) return false;

    const locksForWeek = locksCache[displayedWeek];
    const userLocksCount = locksForWeek.filter(lock =>
      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;

    const maxPicksForWeek = getMaxPicksForWeek(displayedWeek);

    return userLocksCount >= maxPicksForWeek;
  }, [displayedWeek, locksCache, user, getMaxPicksForWeek]);

  // Get games that have locks for generic cards
  const gamesWithLocks = useMemo(() => {
    if (!displayedWeek || !locksCache[displayedWeek]) return [];

    const lockedGameIds = new Set();
    locksCache[displayedWeek].forEach(lock => {
      lockedGameIds.add(lock.gameId);
    });

    // Only include games that are in the current filtered/sorted list
    return sortedGames.filter(game => lockedGameIds.has(game.id));
  }, [sortedGames, locksCache, displayedWeek]);

  // Get all current user's locks for this week
  const userLocksForWeek = useMemo(() => {
    if (!displayedWeek || !locksCache[displayedWeek] || !user) return [];

    const userLocks = locksCache[displayedWeek].filter(lock =>
      lock.userId === user.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    );

    if (userLocks.length === 0) return [];

    // Get all user locks with their corresponding games
    return userLocks
      .map(userLock => {
        const userLockGame = sortedGames.find(game => game.id === userLock.gameId);
        return userLockGame ? { game: userLockGame, lock: userLock } : null;
      })
      .filter(Boolean); // Remove any null entries where game wasn't found
  }, [displayedWeek, locksCache, user, sortedGames]);

  // Determine if we should show generic cards (States 2 & 4 pre-lock)
  const shouldShowGenericCards = useMemo(() => {
    // Don't show generic cards when filters are active - they would reveal which games have locks
    if (hasActiveFilters) {
      return false;
    }
    return !canSeeLocks() && !hasReachedMaxPicks() && gamesWithLocks.length > 0;
  }, [canSeeLocks, hasReachedMaxPicks, gamesWithLocks.length, hasActiveFilters]);

  const handleGameTap = useCallback((game) => {
    setSelectedGame(game);
    setIsDetailOpen(true);
  }, []);

  const handlePickAction = useCallback(async ({ gameId, pickType, lockLine, action }) => {
    try {
      // Ensure we have a valid week number
      const weekToUse = displayedWeek || currentWeek;
      console.log('Mobile pick action:', { gameId, pickType, lockLine, action, displayedWeek, currentWeek, weekToUse });

      if (!weekToUse) {
        console.error('No valid week number available for pick action');
        return;
      }

      if (action === 'watch') {
        // Simulate watch workflow
        await toggleWatchPick(gameId, pickType, weekToUse, lockLine, lockLine, false);
      } else if (action === 'lock') {
        // Simulate lock workflow
        const pickData = {
          gameId,
          pickType,
          lockLine,
          weekNumber: weekToUse
        };
        await lockPicks([pickData], weekToUse);
      }
    } catch (error) {
      console.error('Error handling pick action:', error);
    }
  }, [toggleWatchPick, lockPicks, displayedWeek, currentWeek]);

  const handleCloseDetail = useCallback(() => {
    setIsDetailOpen(false);
    setSelectedGame(null);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.gamesList}>
        {sortedGames.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No games available for this week.</p>
          </div>
        ) : (
          <>
            {/* User's locks for this week */}
            {userLocksForWeek.length > 0 && (
              <div className={styles.userLockSection}>
                <h3 className={styles.userLockHeader}>
                  Your Week {displayedWeek} Lock{userLocksForWeek.length > 1 ? 's' : ''}
                </h3>
                {userLocksForWeek.map((userLockData, index) => (
                  <MobileGameRow
                    key={`user-lock-${userLockData.game.id}-${index}`}
                    game={userLockData.game}
                    displayedWeek={displayedWeek}
                    locksCache={locksCache}
                    onGameTap={handleGameTap}
                    shouldHideLockIndicators={false}
                    isUserLock={true}
                    userLock={userLockData.lock}
                  />
                ))}
              </div>
            )}

            {/* Generic lock cards for States 2 & 4 pre-lock */}
            {shouldShowGenericCards && (
              <div className={styles.genericCardsSection}>
                {gamesWithLocks.map(game => {
                  const gameLocks = locksCache[displayedWeek]?.filter(lock => lock.gameId === game.id) || [];
                  return (
                    <MobileGenericLockCard
                      key={`generic-${game.id}`}
                      game={game}
                      locks={gameLocks}
                      displayedWeek={displayedWeek}
                    />
                  );
                })}
              </div>
            )}

            {/* Regular game cards - exclude user's lock games if they exist */}
            {sortedGames
              .filter(game => !userLocksForWeek.some(userLockData => userLockData.game.id === game.id))
              .map(game => (
                <MobileGameRow
                  key={game.id}
                  game={game}
                  displayedWeek={displayedWeek}
                  locksCache={locksCache}
                  onGameTap={handleGameTap}
                  shouldHideLockIndicators={!canSeeLocks() && !hasReachedMaxPicks()}
                />
              ))}
          </>
        )}
      </div>

      {selectedGame && (
        <MobileGameDetail
          game={selectedGame}
          displayedWeek={displayedWeek}
          isOpen={isDetailOpen}
          onClose={handleCloseDetail}
          onPickAction={handlePickAction}
          hasReachedMaxPicks={hasReachedMaxPicks()}
        />
      )}
    </div>
  );
};

MobileWeeklyPickSheet.propTypes = {
  displayedWeek: PropTypes.number,
  onWeekChange: PropTypes.func.isRequired
};

export default MobileWeeklyPickSheet;
